/**
 * 销售 API hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../client'
import type {
  QuoteListResponse,
  QuoteDetail,
  NegotiationDetail,
  NegotiationListItem,
  AdjustPriceRequest,
  NegotiationFilter,
  QuoteStatus,
} from '../types/sales'

const QUERY_KEY = {
  quotes: (filter?: string) => ['quotes', filter] as const,
  quoteDetail: (id: number) => ['quote-detail', id] as const,
  negotiationDetail: (id: number) => ['negotiation-detail', id] as const,
  negotiations: (filter?: string) => ['negotiations', filter] as const,
}

function normalizeQuoteStatus(status: string): QuoteStatus {
  const statusMap: Record<string, QuoteStatus> = {
    draft: 'draft',
    DRAFT: 'draft',
    negotiating: 'negotiating',
    UNDER_NEGOTIATION: 'negotiating',
    awaiting_confirm: 'awaiting_confirm',
    SUBMITTED: 'awaiting_confirm',
    REVISED: 'awaiting_confirm',
    confirmed: 'confirmed',
    APPROVED: 'confirmed',
    rejected: 'rejected',
    REJECTED: 'rejected',
    cancelled: 'cancelled',
    CANCELLED: 'cancelled',
  }

  return statusMap[status] || 'draft'
}

// 报价单列表
export function useQuotes(
  filter: QuoteStatus | 'all' = 'all',
  page = 0,
  size = 20,
  keyword?: string
) {
  const params = new URLSearchParams()
  params.set('filter', filter)
  params.set('page', String(page))
  params.set('size', String(size))
  if (keyword?.trim()) {
    params.set('keyword', keyword.trim())
  }
  return useQuery({
    queryKey: [...QUERY_KEY.quotes(), filter, page, size, keyword ?? ''],
    queryFn: () =>
      apiClient.get<QuoteListResponse>(`/api/sales/quotes?${params.toString()}`),
  })
}

// 报价单详情
export function useQuoteDetail(id: number) {
  return useQuery({
    queryKey: QUERY_KEY.quoteDetail(id),
    queryFn: async () => {
      const quote = await apiClient.get<QuoteDetail>(`/api/quotations/${id}`)
      return {
        ...quote,
        status: normalizeQuoteStatus(quote.status),
      }
    },
    enabled: !!id,
  })
}

// 洽谈详情
export function useNegotiationDetail(quotationId: number) {
  return useQuery({
    queryKey: QUERY_KEY.negotiationDetail(quotationId),
    queryFn: async () => {
      const negotiation = await apiClient.get<NegotiationDetail>(`/api/quotations/${quotationId}/negotiations`)
      return {
        ...negotiation,
        status: normalizeQuoteStatus(negotiation.status),
      }
    },
    enabled: !!quotationId,
  })
}

// 洽谈记录列表
export function useNegotiations(filter: NegotiationFilter = 'all', page = 0, size = 20) {
  return useQuery({
    queryKey: [...QUERY_KEY.negotiations(), filter, page, size],
    queryFn: () =>
      apiClient.get<{ content: NegotiationListItem[]; totalElements: number; totalPages: number }>(
        `/api/sales/negotiations?filter=${filter}&page=${page}&size=${size}`
      ),
  })
}

// 提交报价
export function useSubmitQuote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ quotationId }: { quotationId: number }) =>
      apiClient.post(`/api/quotations/${quotationId}/submit`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
    },
  })
}

// 调整报价
export function useAdjustPrice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ quotationId, data }: { quotationId: number; data: AdjustPriceRequest }) =>
      apiClient.post(`/api/quotations/${quotationId}/adjust-price`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
    },
  })
}

// 确认成交
export function useApproveQuote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ quotationId }: { quotationId: number }) =>
      apiClient.post(`/api/quotations/${quotationId}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
    },
  })
}

// 拒绝报价
export function useRejectQuote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ quotationId, reason }: { quotationId: number; reason: string }) =>
      apiClient.post(`/api/quotations/${quotationId}/reject`, { feedback: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
    },
  })
}

// 发送提醒
export function useSendReminder() {
  return useMutation({
    mutationFn: ({ quotationId }: { quotationId: number }) =>
      apiClient.post(`/api/quotations/${quotationId}/send-reminder`, {}),
  })
}

// 状态标签配置
export const QUOTE_STATUS_CONFIG: Record<
  QuoteStatus,
  { label: string; color: string; bgColor: string }
> = {
  draft: { label: '草稿', color: '#6b7280', bgColor: '#f3f4f6' },
  negotiating: { label: '洽谈中', color: '#f59e0b', bgColor: '#fef3c7' },
  awaiting_confirm: { label: '待确认', color: '#3b82f6', bgColor: '#dbeafe' },
  confirmed: { label: '已确认', color: '#16a34a', bgColor: '#dcfce7' },
  rejected: { label: '已拒绝', color: '#6b7280', bgColor: '#f3f4f6' },
  cancelled: { label: '已作废', color: '#9ca3af', bgColor: '#f3f4f6' },
}

export type QuoteStatusConfigType = typeof QUOTE_STATUS_CONFIG
